import { useEffect, useMemo, useRef } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { CanvasNote } from '@/src/renderer/lib/db/models'

const isDev = process.env.NODE_ENV !== 'production'

/**
 * Ob zwei Notizen dieselben Daten tragen — Feld für Feld, Datumsfelder nach
 * ihrem Zeitwert.
 *
 * Nicht über `updatedAt`: `setCanvasNoteMeasuredHeight` schreibt bewusst ohne
 * Sync-Buchführung, die gemessene Höhe änderte sich also, ohne dass der
 * Stempel es verriete.
 */
function sameNote(a: CanvasNote, b: CanvasNote): boolean {
  const keys = Object.keys(a) as (keyof CanvasNote)[]
  if (keys.length !== Object.keys(b).length) return false
  for (const key of keys) {
    const x = a[key]
    const y = b[key]
    if (x === y) continue
    if (x instanceof Date && y instanceof Date && x.getTime() === y.getTime()) continue
    return false
  }
  return true
}

/**
 * Die Liste der Datenquelle mit den ALTEN Objekten, wo sich nichts geändert hat.
 *
 * Dexie baut bei jedem Feuern neue Objekte für alle Zeilen. Der Node-Abgleich
 * in `CanvasFlow` vergleicht `data.note` aber per Identität — ohne diesen
 * Schritt wurde nach JEDEM Schreibvorgang (Loslassen, Farbwechsel, gemessene
 * Höhe) jeder Node neu gebaut und jede Notiz neu gerendert, samt der
 * Schriftgrößen-Suche in jedem Notizen.
 */
function shareNotes(previous: CanvasNote[] | undefined, next: CanvasNote[]): CanvasNote[] {
  if (!previous) return next
  const byId = new Map(previous.map(note => [note.id, note]))
  let unchanged = previous.length === next.length
  const shared = next.map((note, index) => {
    const old = byId.get(note.id)
    const kept = old && sameNote(old, note) ? old : note
    if (kept !== previous[index]) unchanged = false
    return kept
  })
  return unchanged ? previous : shared
}

/** Live (non-deleted) Notizen/Memos of one project. `undefined` while loading. */
export function useCanvasNotes(projectId: string | undefined): CanvasNote[] | undefined {
  const raw = useDataSource().useCanvasNotes(projectId)

  const sharedRef = useRef<CanvasNote[] | undefined>(undefined)
  const notes = useMemo(() => {
    if (raw === undefined) return undefined
    const shared = shareNotes(sharedRef.current, raw)
    sharedRef.current = shared
    return shared
  }, [raw])

  // Dev-Diagnose für den Fall „Notizen verschwinden nach ein paar Sekunden":
  // Wird hier eine kürzere Liste gemeldet als zuvor, hat die Live-Query sie
  // verloren — dann liegt es an Dexie oder der Abfrage. Bleibt die Zahl hier
  // gleich, während die Notizen aus dem Bild verschwinden, liegt es am
  // Node-Aufbau in CanvasFlow (das Gegenstück-Log dort).
  //
  // Absichtlich nur bei einem RÜCKGANG laut: ein Log pro Live-Query-Update
  // wäre in der Konsole nicht wiederzufinden.
  const previousCount = useRef<number | null>(null)
  useEffect(() => {
    if (!isDev || notes === undefined) return
    const before = previousCount.current
    previousCount.current = notes.length
    if (before !== null && notes.length < before) {
      console.warn(`[Canvas/notes] Live-Query meldet ${notes.length} statt ${before} Notizen`)
    }
  }, [notes])

  return notes
}
